import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from './lib/axiosClient';
import './SpaMobile.css';

import { useTenant } from './contexts/TenantContext';

const C = {
  bg: '#0a0a12', panel: '#12121e', card: '#16161f', border: '#1e1e2e',
  text: '#f0f0f2', muted: '#7a7a8a', amber: '#f5a623', green: '#3ecf8e',
  red: '#e8484a', blue: '#4f8ef7', purple: '#a78bfa', pink: '#f472b6', teal: '#2dd4bf',
};

const TABS = [
  { key: 'dashboard', icon: '📊', label: 'Dashboard' },
  { key: 'bookings', icon: '📅', label: 'Bookings' },
  { key: 'clients', icon: '👥', label: 'Clients' },
  { key: 'services', icon: '💆', label: 'Services' },
  { key: 'staff', icon: '👩‍⚕️', label: 'Staff' },
  { key: 'members', icon: '👑', label: 'Members' },
  { key: 'reminders', icon: '🔔', label: 'Reminders' },
  { key: 'notes', icon: '📋', label: 'Treatment Notes' },
  { key: 'finance', icon: '💰', label: 'Finance' },
];

const STATUS_COLORS = {
  confirmed: { bg: 'rgba(62,207,142,0.1)', color: '#3ecf8e', label: 'Confirmed' },
  pending: { bg: 'rgba(245,166,35,0.1)', color: '#f5a623', label: 'Pending' },
  completed: { bg: 'rgba(79,142,247,0.1)', color: '#4f8ef7', label: 'Completed' },
  cancelled: { bg: 'rgba(232,72,74,0.1)', color: '#e8484a', label: 'Cancelled' },
};

function Avatar({ name, size = 40, color = '#a78bfa' }) {
  const i = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return <div style={{ width: size, height: size, borderRadius: '50%', background: `linear-gradient(135deg, ${color}, ${color}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.35, color: '#fff', flexShrink: 0 }}>{i}</div>;
}

function StatCard({ icon, label, value, sub, color, onClick }) {
  return (
    <div onClick={onClick} style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 20px', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color: color || C.text }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, width: '100%', maxWidth: 480, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ fontSize: 12, color: C.muted, marginBottom: 4, fontWeight: 600 }}>{label}</div>}
      <input {...props} style={{ width: '100%', padding: '9px 12px', background: C.card, border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', ...props.style }} />
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ fontSize: 12, color: C.muted, marginBottom: 4, fontWeight: 600 }}>{label}</div>}
      <select {...props} style={{ width: '100%', padding: '9px 12px', background: C.card, border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>{children}</select>
    </div>
  );
}

function Btn({ children, color = C.purple, textColor = '#fff', onClick, disabled, style = {} }) {
  return <button onClick={onClick} disabled={disabled} style={{ padding: '9px 20px', background: color, color: textColor, border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13, opacity: disabled ? 0.5 : 1, ...style }}>{children}</button>;
}

// ── DASHBOARD TAB ────────────────────────────────────────────────────────────
function useIsMobile() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => { const fn = () => setW(window.innerWidth); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, []);
  return { isMobile: w < 768, isTablet: w < 1024, width: w };
}

function DashboardTab({ stats, todayBookings, upcomingBookings, revenueChart, memberships, onRebook, onComplete, setMainTab, reload, isMobile }) {
  const vipContactIds = new Set((memberships || []).map(s => s.contact_id).filter(Boolean));
  const [activeModal, setActiveModal] = useState(null);
  const navigate = useNavigate();
  const maxRev = Math.max(...revenueChart.map(d => d.revenue || 0), 1);
  const staffMap = todayBookings.reduce((acc, b) => { const n = b.staff_name || (b.staff ? b.staff.first_name + ' ' + (b.staff.last_name || '') : 'Unassigned'); acc[n] = (acc[n] || 0) + 1; return acc; }, {});

  const newBookings = [...todayBookings, ...upcomingBookings].filter(b => b.status === 'pending');
  return (
    <div>
      {newBookings.length > 0 && (
        <div className='spa-booking-alert' style={{ background: 'linear-gradient(135deg, rgba(62,207,142,0.12), rgba(62,207,142,0.04))', border: '1px solid rgba(62,207,142,0.35)', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: newBookings.length > 1 ? 12 : 0 }}>
            <span style={{ fontSize: 22 }}>🔔</span>
            <span style={{ fontWeight: 800, color: '#3ecf8e', fontSize: 15 }}>{newBookings.length} New Booking{newBookings.length > 1 ? 's' : ''}!</span>
          </div>
          {newBookings.map(b => {
            const cn = b.contact ? (b.contact.first_name + ' ' + (b.contact.last_name || '')) : b.client_name || 'Walk-in';
            const ph = b.contact?.phone || b.client_phone || '';
            const sn = b.services?.name || b.service_name || 'Service';
            const tr = b.start_time || b.booking_time;
            const ts = tr ? (() => { const [h,m] = tr.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; const h12 = h % 12 || 12; return h12 + ':' + String(m).padStart(2,'0') + ' ' + ap; })() : '';
            return (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid rgba(62,207,142,0.15)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3ecf8e', boxShadow: '0 0 8px #3ecf8e', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, color: '#f0f0f2', fontSize: 14 }}>{cn}</span>
                  {ph && <span style={{ color: '#3ecf8e', fontSize: 12, marginLeft: 6 }}>📞 {ph}</span>}
                  <span style={{ color: '#5a5a6a', fontSize: 13 }}> — {sn}</span>
                </div>
                {ts && <span style={{ fontSize: 13, fontWeight: 600, color: '#3ecf8e' }}>{ts}</span>}
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: 'rgba(245,166,35,0.12)', color: '#f5a623' }}>{b.status || 'pending'}</span>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button onClick={() => { axios.put('/api/bookings/' + b.id + '/approve').then(() => reload()); }} style={{ background: 'rgba(62,207,142,0.15)', border: '1px solid rgba(62,207,142,0.3)', borderRadius: 6, padding: '4px 10px', color: '#3ecf8e', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✓ Accept</button>
                  <button onClick={() => { axios.put('/api/bookings/' + b.id + '/decline').then(() => reload()); }} style={{ background: 'rgba(232,72,74,0.12)', border: '1px solid rgba(232,72,74,0.3)', borderRadius: 6, padding: '4px 10px', color: '#e8484a', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✕ Deny</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {memberships.length > 0 && (
        <div style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 12, padding: '12px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>⏰</span>
          <div style={{ flex: 1 }}><span style={{ fontWeight: 700, color: C.amber }}>{memberships.length} membership renewal{memberships.length > 1 ? 's' : ''} due in 7 days</span></div>
          <Btn onClick={() => setMainTab('members')} color="rgba(245,166,35,0.15)" textColor={C.amber} style={{ border: '1px solid rgba(245,166,35,0.3)' }}>View →</Btn>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        <StatCard icon="💰" label="Today's Revenue" value={'$' + Number(stats.todayRevenue || 0).toFixed(0)} color={C.green} onClick={() => setActiveModal('revenue')} />
        <StatCard icon="📅" label="Today's Appts" value={todayBookings.length} sub={todayBookings.filter(b => b.status === 'confirmed').length + ' confirmed'} color={C.purple} onClick={() => setActiveModal('appts')} />
        <StatCard icon="📊" label="This Month" value={'$' + Number(stats.monthRevenue || 0).toFixed(0)} color={C.blue} onClick={() => setActiveModal('month')} />
        <StatCard icon="👥" label="Total Clients" value={stats.totalCustomers || 0} color={C.pink} onClick={() => setActiveModal('clients')} />
        {activeModal === 'revenue' && (
          <Modal title="Today's Revenue" onClose={() => setActiveModal(null)}>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:32,fontWeight:900,color:C.green}}>{'$'+Number(stats.todayRevenue||0).toFixed(0)}</div>
              <div style={{fontSize:12,color:C.muted,marginTop:4}}>Total confirmed today</div>
            </div>
            <div style={{borderTop:'1px solid '+C.border,paddingTop:16,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10}}>BY SERVICE</div>
              {Object.entries(todayBookings.reduce((acc,b)=>{const s=b.services?.name||b.service_name||'Other';acc[s]=(acc[s]||0)+parseFloat(b.final_price||0);return acc;},{})).map(([svc,amt])=>(
                <div key={svc} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid '+C.border}}>
                  <span style={{fontSize:13}}>{svc}</span>
                  <span style={{fontSize:13,fontWeight:700,color:C.green}}>{'$'+amt.toFixed(0)}</span>
                </div>
              ))}
            </div>
            <div style={{borderTop:'1px solid '+C.border,paddingTop:16}}>
              <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10}}>BY THERAPIST</div>
              {Object.entries(todayBookings.reduce((acc,b)=>{const s=b.staff?b.staff.first_name+' '+(b.staff.last_name||''):'Unassigned';acc[s]=(acc[s]||0)+parseFloat(b.final_price||0);return acc;},{})).map(([st,amt])=>(
                <div key={st} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid '+C.border}}>
                  <span style={{fontSize:13}}>{st}</span>
                  <span style={{fontSize:13,fontWeight:700,color:C.purple}}>{'$'+amt.toFixed(0)}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>{setActiveModal(null);navigate('/app/checkout');}} style={{marginTop:20,width:'100%',padding:'12px',background:C.green,border:'none',borderRadius:10,color:'#000',fontWeight:800,cursor:'pointer',fontSize:14}}>Go to Checkout</button>
          </Modal>
        )}
        {activeModal === 'appts' && (
          <Modal title="Today's Appointments" onClose={() => setActiveModal(null)}>
            <div style={{display:'flex',gap:12,marginBottom:16}}>
              <div style={{flex:1,background:'rgba(62,207,142,0.1)',borderRadius:10,padding:'10px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:900,color:C.green}}>{todayBookings.filter(b=>b.status==='confirmed').length}</div>
                <div style={{fontSize:11,color:C.muted}}>Confirmed</div>
              </div>
              <div style={{flex:1,background:'rgba(245,166,35,0.1)',borderRadius:10,padding:'10px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:900,color:'#f5a623'}}>{todayBookings.filter(b=>b.status==='pending').length}</div>
                <div style={{fontSize:11,color:C.muted}}>Pending</div>
              </div>
              <div style={{flex:1,background:'rgba(167,139,250,0.1)',borderRadius:10,padding:'10px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:900,color:C.purple}}>{todayBookings.length}</div>
                <div style={{fontSize:11,color:C.muted}}>Total</div>
              </div>
            </div>
            {todayBookings.map(b=>{
              const name=b.contacts?b.contacts.first_name+' '+b.contacts.last_name:'Guest';
              const svc=b.services?.name||b.service_name||'Appointment';
              const staff=b.staff?b.staff.first_name:'';
              const t=b.start_time?b.start_time.substring(0,5):'';
              const parts=t?t.split(':').map(Number):[0,0];
              const h=parts[0],m=parts[1];
              const timeStr=t?((h%12||12)+':'+(m<10?'0':'')+m+(h>=12?' PM':' AM')):'';
              const sc=STATUS_COLORS[b.status]||STATUS_COLORS.pending;
              return(
                <div key={b.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderBottom:'1px solid '+C.border}}>
                  <div style={{minWidth:60,fontSize:12,fontWeight:700,color:C.purple}}>{timeStr}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700}}>{name}</div>
                    <div style={{fontSize:11,color:C.muted}}>{svc}{staff?' · '+staff:''}</div>
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:C.green}}>{'$'+b.final_price}</div>
                  <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:5,background:sc.bg,color:sc.color}}>{sc.label}</span>
                </div>
              );
            })}
            <button onClick={()=>{setActiveModal(null);setMainTab('bookings');}} style={{marginTop:20,width:'100%',padding:'12px',background:C.purple,border:'none',borderRadius:10,color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14}}>View All Bookings</button>
          </Modal>
        )}
        {activeModal === 'month' && (
          <Modal title="This Month" onClose={() => setActiveModal(null)}>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:32,fontWeight:900,color:C.purple}}>{'$'+Number(stats.monthRevenue||0).toFixed(0)}</div>
              <div style={{fontSize:12,color:C.muted,marginTop:4}}>Revenue this month</div>
            </div>
            <div style={{borderTop:'1px solid '+C.border,paddingTop:16}}>
              <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10}}>TOP SERVICES</div>
              {Object.entries([...todayBookings,...upcomingBookings].reduce((acc,b)=>{const s=b.services?.name||b.service_name||'Other';acc[s]=(acc[s]||0)+1;return acc;},{})).sort((a,b)=>b[1]-a[1]).map(([svc,cnt])=>(
                <div key={svc} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid '+C.border}}>
                  <span style={{fontSize:13}}>{svc}</span>
                  <span style={{fontSize:12,fontWeight:700,color:C.muted}}>{cnt+' booking'+(cnt!==1?'s':'')}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>{setActiveModal(null);setMainTab('finance');}} style={{marginTop:20,width:'100%',padding:'12px',background:C.purple,border:'none',borderRadius:10,color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14}}>View Finance</button>
          </Modal>
        )}
        {activeModal === 'clients' && (
          <Modal title="Clients" onClose={() => setActiveModal(null)}>
            <div style={{display:'flex',gap:12,marginBottom:16}}>
              <div style={{flex:1,background:'rgba(62,207,142,0.1)',borderRadius:10,padding:'10px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:900,color:C.green}}>{stats.totalCustomers||0}</div>
                <div style={{fontSize:11,color:C.muted}}>Total Clients</div>
              </div>
              <div style={{flex:1,background:'rgba(167,139,250,0.1)',borderRadius:10,padding:'10px 14px',textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:900,color:C.purple}}>{memberships.length}</div>
                <div style={{fontSize:11,color:C.muted}}>Members</div>
              </div>
            </div>
            <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10}}>BOOKED TODAY</div>
            {[...new Map(todayBookings.map(b=>[b.contact_id,b])).values()].map(b=>{
              const name=b.contacts?b.contacts.first_name+' '+b.contacts.last_name:'Guest';
              const isVIP=vipContactIds.has(b.contact_id);
              return(
                <div key={b.contact_id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid '+C.border}}>
                  <Avatar name={name} size={30} color={isVIP?'#f5c842':C.purple} />
                  <div style={{flex:1,fontSize:13,fontWeight:600}}>{name}{isVIP&&<span style={{marginLeft:6,fontSize:10,color:'#f5c842'}}>VIP</span>}</div>
                  <div style={{fontSize:11,color:C.muted}}>{b.services?.name||'Appointment'}</div>
                </div>
              );
            })}
            <button onClick={()=>{setActiveModal(null);setMainTab('clients');}} style={{marginTop:20,width:'100%',padding:'12px',background:'rgba(236,72,153,0.8)',border:'none',borderRadius:10,color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14}}>View All Clients</button>
          </Modal>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>📅 Today's Schedule</div>
          {todayBookings.length === 0 ? (
            <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🌿</div>
              <div style={{ color: C.muted, fontSize: 14 }}>No appointments today</div>
              <Btn onClick={() => setMainTab('bookings')} style={{ marginTop: 14 }}>+ Book Appointment</Btn>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {todayBookings.map(b => <BookingCard key={b.id} booking={b} onRebook={onRebook} onComplete={onComplete} vipContactIds={vipContactIds} />)}
            </div>
          )}
          {upcomingBookings.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14 }}>🗓 Upcoming</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {upcomingBookings.slice(0, 5).map(b => <BookingCard key={b.id} booking={b} onRebook={onRebook} onComplete={onComplete} vipContactIds={vipContactIds} />)}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 20px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 14 }}>📈 Last 7 Days</div>
            {revenueChart.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 72 }}>
                {revenueChart.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div title={'$' + (d.revenue || 0).toFixed(0)} style={{ width: '100%', height: Math.max(4, (d.revenue / maxRev) * 60), background: d.revenue > 0 ? 'linear-gradient(180deg,' + C.purple + ',' + C.purple + '55)' : C.border, borderRadius: '4px 4px 0 0' }} />
                    <div style={{ fontSize: 9, color: C.muted }}>{new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  </div>
                ))}
              </div>
            ) : <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No data yet</div>}
          </div>

          <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 20px' }}>
            <div onClick={() => setMainTab('staff')} style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 14, cursor: 'pointer' }}>👩‍⚕️ Staff Today <span style={{ float: 'right', color: C.orange }}>→</span></div>
            {Object.keys(staffMap).length === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>No bookings today</div> :
              Object.entries(staffMap).map(([name, count]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <Avatar name={name} size={30} color={C.teal} />
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{name}</div>
                  <span style={{ fontSize: 12, color: C.muted }}>{count} appt{count > 1 ? 's' : ''}</span>
                </div>
              ))
            }
          </div>

          <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 20px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 14 }}>Quick Actions</div>
            {[['💳 New Checkout', () => navigate('/app/checkout'), C.green], ['📋 Treatment Notes', () => setMainTab('notes'), C.teal], ['🎁 Gift Cards', () => navigate('/app/gift-cards'), C.pink], ['📲 SMS Campaign', () => navigate('/app/sms-campaigns'), C.blue]].map(([label, action, color]) => (
              <button key={label} onClick={action} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: C.panel, border: '1px solid ' + C.border, borderRadius: 8, cursor: 'pointer', marginBottom: 6, color: C.text, fontSize: 13, fontWeight: 600 }}>
                {label} <span style={{ marginLeft: 'auto', color: C.muted }}>→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── BOOKING CARD ─────────────────────────────────────────────────────────────
function getTimeTint(h) {
  if (!h && h !== 0) return 'transparent';
  if (h < 12) return 'rgba(96,165,250,0.18)';   // morning - cool blue
  if (h < 17) return 'rgba(167,139,250,0.15)';  // afternoon - warm purple
  return 'rgba(99,102,241,0.18)';                // evening - deep indigo
}
function BookingCard({ booking, onRebook, onComplete, vipContactIds = new Set() }) {
  const timeRaw = booking.start_time ? booking.start_time.substring(0, 5) : '';
  const timeStr = timeRaw ? (() => { const [h,m] = timeRaw.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; const h12 = h % 12 || 12; return h12 + ':' + String(m).padStart(2,'0') + ' ' + ap; })() : '';
  const dateStr = booking.booking_date ? new Date(booking.booking_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  const rawHour = booking.start_time ? parseInt(booking.start_time.substring(0, 2)) : 0;
  const sc = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
  const name = booking.contact_name || booking.customer_name ||
    (booking.contacts ? booking.contacts.first_name + ' ' + (booking.contacts.last_name || '') : 'Guest');
  const svcName = (booking.services?.name) || booking.service_name || 'Appointment';
  const staffName = booking.staff_name || (booking.staff ? booking.staff.first_name + ' ' + (booking.staff.last_name || '') : '');
  const contactId = booking.contact_id || booking.contacts?.id;
  const isVIP = vipContactIds.has(contactId);
  const hasNotes = !!(booking.notes && booking.notes.trim());
  const timeTint = getTimeTint(rawHour);
  return (
    <div style={{
      background: timeTint !== 'transparent' ? timeTint : C.card,
      border: isVIP ? '1.5px solid #f5c842' : '1px solid ' + C.border,
      borderRadius: 12,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      boxShadow: isVIP ? '0 0 0 1px rgba(245,200,66,0.2)' : 'none',
    }}>
      <div style={{ textAlign: 'center', minWidth: 52, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: C.purple }}>{timeStr}</div>
        <div style={{ fontSize: 10, color: C.muted }}>{dateStr}</div>
      </div>
      <div style={{ width: 1, height: 36, background: C.border }} />
      <div style={{ position: 'relative' }}>
        <Avatar name={name} size={34} color={isVIP ? '#f5c842' : C.purple} />
        {isVIP && <span style={{ position: 'absolute', top: -6, right: -6, fontSize: 12 }}>👑</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}{isVIP && <span style={{ marginLeft: 5, fontSize: 10, color: '#f5c842', fontWeight: 700 }}>VIP</span>}
        </div>
        <div style={{ fontSize: 11, color: C.muted }}>
          {svcName}{staffName ? ' · ' + staffName : ''}
          {hasNotes && <span title={booking.notes} style={{ marginLeft: 6, cursor: 'help' }}>📝</span>}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>{sc.label}</span>
      <div style={{ display: 'flex', gap: 4 }}>
        {booking.status === 'confirmed' && <button onClick={() => onComplete(booking)} style={{ padding: '4px 8px', background: 'rgba(62,207,142,0.1)', border: '1px solid rgba(62,207,142,0.2)', borderRadius: 6, color: C.green, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>✓</button>}
        <button onClick={() => onRebook(booking)} style={{ padding: '4px 8px', background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 6, color: C.purple, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>↩</button>
      </div>
    </div>
  );
}

// ── BOOKINGS TAB ─────────────────────────────────────────────────────────────
function BookingsTab({ todayBookings, upcomingBookings, onRebook, onComplete, reload, memberships = [] }) {
  const vipContactIds = new Set(memberships.map(s => s.contact_id).filter(Boolean));
  const [showNew, setShowNew] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ contact_id: '', service_id: '', staff_id: '', start_time: '', notes: '', status: 'confirmed' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (showNew) {
      axios.get('/api/customers?limit=200').then(r => setContacts(Array.isArray(r.data) ? r.data : r.data?.customers || r.data?.contacts || r.data?.data || []));
      axios.get('/api/services?active_only=true').then(r => setServices(Array.isArray(r.data) ? r.data : r.data?.services || r.data?.data || []));
      axios.get('/api/staff').then(r => setStaff(r.data || []));
    }
  }, [showNew]);

  async function saveBooking() {
    if (!form.contact_id || !form.start_time) return alert('Client and time required');
    setSaving(true);
    try {
      const svc = services.find(s => s.id === parseInt(form.service_id));
      await axios.post('/api/bookings', { ...form, service_name: svc?.name || '', final_price: svc?.price || 0 });
      setShowNew(false); reload();
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  }

  const all = [...todayBookings, ...upcomingBookings];
  const filtered = filter === 'all' ? all : all.filter(b => b.status === filter);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {['all', 'confirmed', 'pending', 'completed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 14px', background: filter === f ? C.purple : C.card, color: filter === f ? '#fff' : C.muted, border: '1px solid ' + (filter === f ? C.purple : C.border), borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, textTransform: 'capitalize' }}>{f}</button>
          ))}
        </div>
        <Btn onClick={() => setShowNew(true)}>+ New Booking</Btn>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: C.muted }}>No bookings found</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(b => <BookingCard key={b.id} booking={b} onRebook={onRebook} onComplete={onComplete} vipContactIds={vipContactIds} />)}
        </div>
      )}

      {showNew && (
        <Modal title="📅 New Booking" onClose={() => setShowNew(false)}>
          <Select label="Client" value={form.contact_id} onChange={e => setForm({ ...form, contact_id: e.target.value })}>
            <option value="">Select client...</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </Select>
          <Select label="Service" value={form.service_id} onChange={e => setForm({ ...form, service_id: e.target.value })}>
            <option value="">Select service...</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.name} — ${s.price}</option>)}
          </Select>
          <Select label="Therapist" value={form.staff_id} onChange={e => setForm({ ...form, staff_id: e.target.value })}>
            <option value="">Any available</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </Select>
          <Input label="Date & Time" type="datetime-local" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} />
          <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any special requests..." />
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <Btn onClick={() => setShowNew(false)} color={C.card} textColor={C.muted} style={{ border: '1px solid ' + C.border, flex: 1 }}>Cancel</Btn>
            <Btn onClick={saveBooking} disabled={saving} style={{ flex: 2 }}>{saving ? 'Saving...' : '✅ Book Appointment'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CLIENTS TAB ──────────────────────────────────────────────────────────────
function ClientsTab({ setMainTab }) {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/customers?limit=200').then(r => { setContacts(Array.isArray(r.data) ? r.data : r.data?.customers || r.data?.contacts || r.data?.data || []); setLoading(false); });
  }, []);

  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    return !q || (c.first_name + ' ' + c.last_name).toLowerCase().includes(q) || (c.phone || '').includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  async function saveClient() {
    if (!form.first_name) return alert('First name required');
    setSaving(true);
    try {
      const r = await axios.post('/api/customers', form);
      setContacts([r.data.contact || r.data, ...contacts]);
      setShowNew(false); setForm({ first_name: '', last_name: '', email: '', phone: '', notes: '' });
    } catch(e) { alert('Error saving client'); }
    setSaving(false);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." style={{ flex: 1, padding: '8px 12px', background: C.card, border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' }} />
          <Btn onClick={() => setShowNew(true)} style={{ padding: '8px 12px' }}>+</Btn>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 20 }}>Loading...</div> :
            filtered.map(c => (
              <div key={c.id} onClick={() => setSelected(c)} style={{ padding: '10px 14px', background: selected?.id === c.id ? 'rgba(167,139,250,0.15)' : C.card, border: '1px solid ' + (selected?.id === c.id ? C.purple : C.border), borderRadius: 10, cursor: 'pointer' }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{c.first_name} {c.last_name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{c.phone || c.email || 'No contact info'}</div>
                {c.loyalty_points > 0 && <div style={{ fontSize: 10, color: C.amber, marginTop: 2 }}>⭐ {c.loyalty_points} pts</div>}
              </div>
            ))
          }
        </div>
      </div>

      <div>
        {!selected ? (
          <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '48px', textAlign: 'center', color: C.muted }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>👥</div>
            <div>Select a client to view their profile</div>
          </div>
        ) : (
          <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid ' + C.border }}>
              <Avatar name={selected.first_name + ' ' + selected.last_name} size={52} color={C.purple} />
              <div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{selected.first_name} {selected.last_name}</div>
                <div style={{ fontSize: 13, color: C.muted }}>{selected.phone} {selected.email ? '· ' + selected.email : ''}</div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <Btn onClick={() => setMainTab('notes')} color={C.teal} textColor="#000" style={{ fontSize: 12 }}>📋 Notes</Btn>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ background: C.panel, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.green }}>{selected.total_visits || 0}</div>
                <div style={{ fontSize: 11, color: C.muted }}>Total Visits</div>
              </div>
              <div style={{ background: C.panel, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.purple }}>{'$'+(selected.total_spent || 0)}</div>
                <div style={{ fontSize: 11, color: C.muted }}>Total Spent</div>
              </div>
              <div style={{ background: C.panel, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.amber }}>{selected.loyalty_points || 0} pts</div>
                <div style={{ fontSize: 11, color: C.muted }}>Loyalty Points</div>
              </div>
              <div style={{ background: C.panel, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: selected.is_vip ? '#f5c842' : C.muted }}>{selected.is_vip ? '👑 VIP' : 'Regular'}</div>
                <div style={{ fontSize: 11, color: C.muted }}>Status</div>
              </div>
            </div>
            {selected.notes && (
              <div style={{ background: C.panel, borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                <div style={{ fontSize: 13, color: C.text }}>{selected.notes}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {showNew && (
        <Modal title="👥 New Client" onClose={() => setShowNew(false)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input label="First Name *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
            <Input label="Last Name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <Input label="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          <Input label="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Allergies, preferences..." />
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <Btn onClick={() => setShowNew(false)} color={C.card} textColor={C.muted} style={{ border: '1px solid ' + C.border, flex: 1 }}>Cancel</Btn>
            <Btn onClick={saveClient} disabled={saving} style={{ flex: 2 }}>{saving ? 'Saving...' : '✅ Add Client'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── SERVICES TAB ─────────────────────────────────────────────────────────────
function ServicesTab() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', duration: 60, category: 'Massage', is_active: true });
  const [saving, setSaving] = useState(false);

  const load = () => axios.get('/api/services').then(r => { setServices(Array.isArray(r.data) ? r.data : r.data?.services || r.data?.data || []); setLoading(false); });
  useEffect(() => { load(); }, []);

  function openEdit(svc) { setEditing(svc); setForm({ name: svc.name, description: svc.description || '', price: svc.price, duration: svc.duration || 60, category: svc.category || 'Massage', is_active: svc.is_active !== false }); setShowNew(true); }

  async function save() {
    if (!form.name || !form.price) return alert('Name and price required');
    setSaving(true);
    try {
      if (editing) await axios.put('/api/services/' + editing.id, form);
      else await axios.post('/api/services', form);
      setShowNew(false); setEditing(null); load();
    } catch(e) { alert('Error saving service'); }
    setSaving(false);
  }

  const cats = [...new Set(services.map(s => s.category).filter(Boolean))];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>💆 Service Menu — {services.length} services</div>
        <Btn onClick={() => { setEditing(null); setForm({ name: '', description: '', price: '', duration: 60, category: 'Massage', is_active: true }); setShowNew(true); }}>+ Add Service</Btn>
      </div>

      {loading ? <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading...</div> :
        cats.length === 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {services.map(s => <ServiceCard key={s.id} service={s} onEdit={openEdit} />)}
          </div>
        ) : cats.map(cat => (
          <div key={cat} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 12 }}>{cat}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {services.filter(s => s.category === cat).map(s => <ServiceCard key={s.id} service={s} onEdit={openEdit} />)}
            </div>
          </div>
        ))
      }

      {showNew && (
        <Modal title={editing ? '✏️ Edit Service' : '💆 New Service'} onClose={() => { setShowNew(false); setEditing(null); }}>
          <Input label="Service Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Swedish Massage" />
          <Input label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input label="Price ($) *" type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
            <Input label="Duration (min)" type="number" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} />
          </div>
          <Select label="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {['Massage', 'Facial', 'Body Treatment', 'Waxing', 'Nails', 'Other'].map(c => <option key={c}>{c}</option>)}
          </Select>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <Btn onClick={() => { setShowNew(false); setEditing(null); }} color={C.card} textColor={C.muted} style={{ border: '1px solid ' + C.border, flex: 1 }}>Cancel</Btn>
            <Btn onClick={save} disabled={saving} style={{ flex: 2 }}>{saving ? 'Saving...' : '✅ Save Service'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ServiceCard({ service, onEdit }) {
  return (
    <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{service.name}</div>
          {service.description && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{service.description}</div>}
          <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: C.green }}>${service.price}</span>
            {service.duration && <span style={{ fontSize: 12, color: C.muted, alignSelf: 'flex-end', marginBottom: 2 }}>{service.duration}min</span>}
          </div>
        </div>
        <button onClick={() => onEdit(service)} style={{ padding: '4px 10px', background: C.panel, border: '1px solid ' + C.border, borderRadius: 6, color: C.muted, cursor: 'pointer', fontSize: 12 }}>Edit</button>
      </div>
    </div>
  );
}

// ── STAFF TAB ────────────────────────────────────────────────────────────────
function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', role: 'Therapist', commission_rate: 35 });
  const [saving, setSaving] = useState(false);

  const [analytics, setAnalytics] = useState({});
  const load = () => Promise.all([axios.get('/api/staff'), axios.get('/api/staff-analytics/compare/all')]).then(([sr, ar]) => { setStaff(Array.isArray(sr.data) ? sr.data : sr.data?.staff || sr.data?.data || []); const map = {}; (ar.data.staff_metrics || []).forEach(m => { map[m.staff_id] = m; }); setAnalytics(map); setLoading(false); });
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.first_name || !form.email) return alert('Name and email required');
    setSaving(true);
    try {
      await axios.post('/api/staff', form);
      setShowNew(false); load();
    } catch(e) { alert('Error saving staff'); }
    setSaving(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>{'👩‍⚕️ Staff'}</div>
        <Btn onClick={() => setShowNew(true)}>+ Add Staff</Btn>
      </div>

      {loading ? <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading...</div> :
        staff.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: C.muted, background: C.card, borderRadius: 14, border: '1px solid ' + C.border }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>👩‍⚕️</div>
            <div>No staff added yet</div>
            <Btn onClick={() => setShowNew(true)} style={{ marginTop: 14 }}>+ Add First Staff Member</Btn>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {staff.map(s => (
              <div key={s.id} style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '20px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  <Avatar name={s.first_name + ' ' + s.last_name} size={44} color={C.teal} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{s.first_name} {s.last_name}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{s.role || 'Staff'}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ background: C.panel, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.green }}>{s.commission_rate || 0}%</div>
                    <div style={{ fontSize: 10, color: C.muted }}>Commission</div>
                  </div>
                  <div style={{ background: C.panel, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: s.active !== false ? C.green : C.red }}>{s.active !== false ? 'Active' : 'Inactive'}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>Status</div>
                  </div>
                  <div style={{ background: C.panel, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.purple }}>{'$'+(analytics[s.id]?.revenue||0)}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>Revenue</div>
                  </div>
                  <div style={{ background: C.panel, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.teal }}>{'$'+(analytics[s.id]?.commission||0)}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>Earned</div>
                  </div>
                </div>
                {s.phone && <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>📞 {s.phone}</div>}
                {s.email && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>✉️ {s.email}</div>}
              </div>
            ))}
          </div>
        )
      }

      {showNew && (
        <Modal title="👩‍⚕️ Add Staff Member" onClose={() => setShowNew(false)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input label="First Name *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
            <Input label="Last Name" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <Input label="Email *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <Input label="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          <Input label="Role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g. Lead Therapist" />
          <Input label="Commission Rate (%)" type="number" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: e.target.value })} />
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <Btn onClick={() => setShowNew(false)} color={C.card} textColor={C.muted} style={{ border: '1px solid ' + C.border, flex: 1 }}>Cancel</Btn>
            <Btn onClick={save} disabled={saving} style={{ flex: 2 }}>{saving ? 'Saving...' : '✅ Add Staff'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── MEMBERS TAB ──────────────────────────────────────────────────────────────
function RemindersTab() {
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get('/api/reminders/log'),
      axios.get('/api/reminders/settings'),
    ]).then(([logRes, settingsRes]) => {
      setLog(Array.isArray(logRes.data) ? logRes.data : logRes.data?.log || logRes.data?.data || []);
      setLoading(false);
    });
    // Load preview of tomorrow's reminders
    axios.get('/api/sms-reminders/preview').then(r => setPreview(r.data)).catch(() => {
      // fallback: use bookings endpoint
    });
  }, []);

  async function sendReminders() {
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post('/api/reminders/check');
      setResult(r.data);
      const logRes = await axios.get('/api/reminders/log');
      setLog(Array.isArray(logRes.data) ? logRes.data : logRes.data?.log || logRes.data?.data || []);
    } catch(e) { setResult({ error: e.message }); }
    setSending(false);
  }

  const recentLog = log.slice(0, 20);
  const smsSent = log.filter(l => l.type === 'sms' && l.status === 'sent').length;
  const smsLogged = log.filter(l => l.type === 'sms' && (l.status === 'logged' || l.status === 'sent')).length;
  const emailSent = log.filter(l => l.type === 'email' && l.status === 'sent').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>🔔 SMS Reminders</div>
        <Btn onClick={sendReminders} disabled={sending} color={C.green} textColor="#000">
          {sending ? 'Sending...' : '📤 Send Tomorrows Reminders'}
        </Btn>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: C.green }}>{smsLogged}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>SMS Sent / Logged</div>
        </div>
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: C.blue }}>{emailSent}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Emails Sent</div>
        </div>
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: C.purple }}>{log.length}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Total Notifications</div>
        </div>
      </div>

      {result && (
        <div style={{ background: result.error ? 'rgba(232,72,74,0.1)' : 'rgba(62,207,142,0.1)', border: '1px solid ' + (result.error ? C.red : C.green), borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
          {result.error ? 'Error: ' + result.error : 'Done! SMS logged: ' + result.smsLogged + ' | Emails sent: ' + result.emailsSent}
        </div>
      )}

      <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '20px' }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>Recent Notification Log</div>
        {loading ? <div style={{ color: C.muted, textAlign: 'center', padding: 20 }}>Loading...</div> :
          recentLog.length === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>No notifications sent yet</div> :
          recentLog.map((l, i) => {
            const sc = l.status === 'sent' ? C.green : l.status === 'logged' ? C.amber : C.red;
            return (
              <div key={i} onClick={() => setSelectedLog(l)} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + C.border, cursor: 'pointer' }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: l.type === 'sms' ? 'rgba(167,139,250,0.15)' : 'rgba(79,142,247,0.15)', color: l.type === 'sms' ? C.purple : C.blue, minWidth: 40, textAlign: 'center' }}>{l.type?.toUpperCase()}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{l.contacts ? l.contacts.first_name + ' ' + (l.contacts.last_name || '') : l.recipient}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{l.recipient}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{l.message?.substring(0, 70)}{l.message?.length > 70 ? '...' : ''}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: sc }}>{l.status}</span>
              </div>
            );
          })
        }
      </div>
      {selectedLog && (
        <Modal title={selectedLog.type === 'sms' ? 'SMS Preview' : 'Email Preview'} onClose={() => setSelectedLog(null)}>
          <div style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: selectedLog.type === 'sms' ? 'rgba(167,139,250,0.2)' : 'rgba(79,142,247,0.2)', color: selectedLog.type === 'sms' ? C.purple : C.blue }}>{selectedLog.type?.toUpperCase()}</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5, background: selectedLog.status === 'sent' ? 'rgba(62,207,142,0.2)' : selectedLog.status === 'logged' ? 'rgba(245,166,35,0.2)' : 'rgba(232,72,74,0.2)', color: selectedLog.status === 'sent' ? C.green : selectedLog.status === 'logged' ? C.amber : C.red }}>{selectedLog.status?.toUpperCase()}</span>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4 }}>TO</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{selectedLog.contacts ? selectedLog.contacts.first_name + ' ' + (selectedLog.contacts.last_name || '') : selectedLog.recipient}</div>
            <div style={{ fontSize: 12, color: C.muted }}>{selectedLog.recipient}</div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8 }}>MESSAGE</div>
          {selectedLog.type === 'email' ? (
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, maxHeight: 400, overflowY: 'auto' }} dangerouslySetInnerHTML={{ __html: selectedLog.message }} />
          ) : (
            <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, padding: 16, fontSize: 14, lineHeight: 1.6, color: C.text }}>
              {selectedLog.message}
            </div>
          )}
          {selectedLog.error_message && (
            <div style={{ marginTop: 14, background: 'rgba(232,72,74,0.1)', border: '1px solid ' + C.red, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.red }}>
              {selectedLog.error_message}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function MembersTab() {
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get('/api/memberships/plans').catch(() => ({ data: [] })),
      axios.get('/api/memberships/subscriptions').catch(() => ({ data: {} })),
    ]).then(([p, s]) => {
      setPlans(Array.isArray(p.data) ? p.data : p.data?.plans || p.data?.data || []);
      setSubs(Array.isArray(s.data) ? s.data : s.data?.subscriptions || s.data?.data || []);
      setLoading(false);
    });
  }, []);

  const renewingSoon = subs.filter(s => {
    if (s.status !== 'active' || !s.next_billing) return false;
    const diff = (new Date(s.next_billing_date) - new Date()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  });

  return (
    <div>
      {renewingSoon.length > 0 && (
        <div style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: C.amber, marginBottom: 8 }}>⏰ {renewingSoon.length} renewal{renewingSoon.length > 1 ? 's' : ''} in next 7 days</div>
          {renewingSoon.map(s => (
            <div key={s.id} style={{ fontSize: 13, color: C.text }}>{s.contact_name || 'Member'} — renews {new Date(s.next_billing).toLocaleDateString()}</div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 14 }}>📋 Plans</div>
          {loading ? <div style={{ color: C.muted }}>Loading...</div> :
            plans.length === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>No plans yet — create one in Memberships</div> :
            plans.map(p => (
              <div key={p.id} style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 12, padding: '16px', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: C.green, margin: '6px 0' }}>${p.price}<span style={{ fontSize: 12, color: C.muted, fontWeight: 400 }}>/{p.billing_cycle || 'month'}</span></div>
                {p.perks && <div style={{ fontSize: 12, color: C.muted }}>{p.perks}</div>}
              </div>
            ))
          }
        </div>

        <div>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 14 }}>👑 Active Members ({subs.filter(s => s.status === 'active').length})</div>
          {loading ? <div style={{ color: C.muted }}>Loading...</div> :
            subs.filter(s => s.status === 'active').length === 0 ? <div style={{ color: C.muted, fontSize: 13 }}>No active members</div> :
            subs.filter(s => s.status === 'active').map(s => (
              <div key={s.id} style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 12, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar name={s.contact_name || 'M'} size={34} color={C.purple} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{s.contact_name || 'Member'}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{s.plan_name} · renews {s.next_billing ? new Date(s.next_billing).toLocaleDateString() : 'N/A'}</div>
                </div>
                <span style={{ fontSize: 11, padding: '3px 8px', background: 'rgba(62,207,142,0.1)', color: C.green, borderRadius: 6, fontWeight: 700 }}>Active</span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

// ── TREATMENT NOTES TAB ──────────────────────────────────────────────────────
function NotesTab() {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], service: '', therapist: '', pressure: 'medium', areas_of_focus: '', areas_to_avoid: '', products_used: '', client_feedback: '', next_session_notes: '', health_flags: '', private_notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get('/api/customers?limit=200').then(r => setContacts(Array.isArray(r.data) ? r.data : r.data?.customers || r.data?.contacts || r.data?.data || [])); }, []);

  useEffect(() => {
    if (selected) axios.get('/api/treatment-notes?contact_id=' + selected.id).then(r => setNotes(Array.isArray(r.data) ? r.data : r.data?.notes || r.data?.data || []));
  }, [selected]);

  const filtered = contacts.filter(c => !search || (c.first_name + ' ' + c.last_name).toLowerCase().includes(search.toLowerCase()));

  async function saveNote() {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post('/api/treatment-notes', { ...form, contact_id: selected.id });
      setShowForm(false);
      const r = await axios.get('/api/treatment-notes?contact_id=' + selected.id);
      setNotes(Array.isArray(r.data) ? r.data : r.data?.notes || r.data?.data || []);
    } catch(e) { alert('Error saving note'); }
    setSaving(false);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>
      <div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." style={{ width: '100%', padding: '8px 12px', background: C.card, border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '70vh', overflowY: 'auto' }}>
          {filtered.map(c => (
            <div key={c.id} onClick={() => setSelected(c)} style={{ padding: '10px 14px', background: selected?.id === c.id ? 'rgba(167,139,250,0.15)' : C.card, border: '1px solid ' + (selected?.id === c.id ? C.purple : C.border), borderRadius: 10, cursor: 'pointer' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{c.first_name} {c.last_name}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{c.phone || 'No phone'}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        {!selected ? (
          <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: 48, textAlign: 'center', color: C.muted }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
            <div>Select a client to view treatment notes</div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>📋 {selected.first_name} {selected.last_name} — Treatment History</div>
              <Btn onClick={() => setShowForm(true)}>+ New Note</Btn>
            </div>
            {notes.length === 0 ? (
              <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: 40, textAlign: 'center', color: C.muted }}>No treatment notes yet</div>
            ) : notes.map(n => (
              <div key={n.id} style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 12, padding: '18px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{n.service || 'Session'}</span>
                    {n.therapist && <span style={{ color: C.muted, fontSize: 12, marginLeft: 8 }}>· {n.therapist}</span>}
                  </div>
                  <span style={{ fontSize: 12, color: C.muted }}>{n.date}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
                  {n.pressure && <div><span style={{ color: C.muted }}>Pressure: </span><span style={{ color: C.text, fontWeight: 600, textTransform: 'capitalize' }}>{n.pressure}</span></div>}
                  {n.areas_of_focus && <div><span style={{ color: C.muted }}>Focus: </span><span style={{ color: C.text }}>{n.areas_of_focus}</span></div>}
                  {n.areas_to_avoid && <div><span style={{ color: C.red }}>Avoid: </span><span style={{ color: C.text }}>{n.areas_to_avoid}</span></div>}
                  {n.products_used && <div><span style={{ color: C.muted }}>Products: </span><span style={{ color: C.text }}>{n.products_used}</span></div>}
                </div>
                {n.health_flags && <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(232,72,74,0.08)', borderRadius: 8, fontSize: 12, color: C.red }}>⚠️ {n.health_flags}</div>}
                {n.next_session_notes && <div style={{ marginTop: 8, fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Next: {n.next_session_notes}</div>}
                {n.private_notes && <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(167,139,250,0.08)', borderRadius: 8, fontSize: 12, color: C.purple }}>🔒 {n.private_notes}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <Modal title="📋 New Treatment Note" onClose={() => setShowForm(false)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input label="Date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            <Input label="Service" value={form.service} onChange={e => setForm({ ...form, service: e.target.value })} placeholder="e.g. Swedish Massage" />
          </div>
          <Input label="Therapist" value={form.therapist} onChange={e => setForm({ ...form, therapist: e.target.value })} />
          <Select label="Pressure" value={form.pressure} onChange={e => setForm({ ...form, pressure: e.target.value })}>
            {['light', 'medium', 'firm', 'deep'].map(p => <option key={p} value={p} style={{ textTransform: 'capitalize' }}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </Select>
          <Input label="Areas of Focus" value={form.areas_of_focus} onChange={e => setForm({ ...form, areas_of_focus: e.target.value })} placeholder="e.g. neck, shoulders" />
          <Input label="Areas to Avoid" value={form.areas_to_avoid} onChange={e => setForm({ ...form, areas_to_avoid: e.target.value })} placeholder="e.g. lower back" />
          <Input label="Products Used" value={form.products_used} onChange={e => setForm({ ...form, products_used: e.target.value })} />
          <Input label="Health Flags" value={form.health_flags} onChange={e => setForm({ ...form, health_flags: e.target.value })} placeholder="Allergies, conditions..." />
          <Input label="Client Feedback" value={form.client_feedback} onChange={e => setForm({ ...form, client_feedback: e.target.value })} />
          <Input label="Next Session Notes" value={form.next_session_notes} onChange={e => setForm({ ...form, next_session_notes: e.target.value })} />
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: C.purple, marginBottom: 4, fontWeight: 600 }}>🔒 Private Notes (Staff Only)</div>
            <textarea value={form.private_notes} onChange={e => setForm({ ...form, private_notes: e.target.value })} rows={2} style={{ width: '100%', padding: '9px 12px', background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 8, color: C.text, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={() => setShowForm(false)} color={C.card} textColor={C.muted} style={{ border: '1px solid ' + C.border, flex: 1 }}>Cancel</Btn>
            <Btn onClick={saveNote} disabled={saving} style={{ flex: 2 }}>{saving ? 'Saving...' : '✅ Save Note'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── FINANCE TAB ──────────────────────────────────────────────────────────────
function FinanceTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ type: 'income', category: 'Services', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);

  const load = () => axios.get('/api/finance').then(r => { setEntries(Array.isArray(r.data) ? r.data : r.data?.entries || r.data?.data || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.amount || !form.description) return alert('Amount and description required');
    setSaving(true);
    try {
      await axios.post('/api/finance', { ...form, amount: form.type === 'expense' ? -Math.abs(parseFloat(form.amount)) : Math.abs(parseFloat(form.amount)) });
      setShowNew(false); load();
    } catch(e) { alert('Error saving entry'); }
    setSaving(false);
  }

  const income = entries.filter(e => e.type === 'income').reduce((s, e) => s + Math.abs(parseFloat(e.amount || 0)), 0);
  const expense = entries.filter(e => e.type === 'expense').reduce((s, e) => s + Math.abs(parseFloat(e.amount || 0)), 0);
  const net = income - expense;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard icon="📈" label="Total Income" value={'$' + income.toFixed(0)} color={C.green} />
        <StatCard icon="📉" label="Total Expenses" value={'$' + expense.toFixed(0)} color={C.red} />
        <StatCard icon="💰" label="Net Profit" value={'$' + net.toFixed(0)} color={net >= 0 ? C.green : C.red} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>Transaction History</div>
        <Btn onClick={() => setShowNew(true)}>+ Add Entry</Btn>
      </div>

      {loading ? <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading...</div> :
        entries.length === 0 ? <div style={{ color: C.muted, textAlign: 'center', padding: 40, background: C.card, borderRadius: 14, border: '1px solid ' + C.border }}>No entries yet</div> :
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.slice(0, 50).map((e, i) => (
            <div key={i} style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 20 }}>{e.type === 'income' ? '📈' : '📉'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{e.description}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{e.category} · {e.date ? new Date(e.date).toLocaleDateString() : ''}</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: e.type === 'income' ? C.green : C.red }}>
                {e.type === 'income' ? '+' : '-'}${Math.abs(parseFloat(e.amount || 0)).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      }

      {showNew && (
        <Modal title="💰 Add Finance Entry" onClose={() => setShowNew(false)}>
          <Select label="Type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </Select>
          <Select label="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {['Services', 'Retail', 'Memberships', 'Gift Cards', 'Rent', 'Payroll', 'Supplies', 'Marketing', 'Other'].map(c => <option key={c}>{c}</option>)}
          </Select>
          <Input label="Amount ($)" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
          <Input label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <Input label="Date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <Btn onClick={() => setShowNew(false)} color={C.card} textColor={C.muted} style={{ border: '1px solid ' + C.border, flex: 1 }}>Cancel</Btn>
            <Btn onClick={save} disabled={saving} style={{ flex: 2 }}>{saving ? 'Saving...' : '✅ Save Entry'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function SpaDashboard() {
  const { businessName, businessType } = useTenant();
  const bt = (businessType || '').toLowerCase();
  const [mainTab, setMainTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [todayBookings, setTodayBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [revenueChart, setRevenueChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebookTarget, setRebookTarget] = useState(null);
  const [rebookDate, setRebookDate] = useState('');
  const [rebookTime, setRebookTime] = useState('10:00');
  const [rebookSaving, setRebookSaving] = useState(false);
  const { isMobile, isTablet } = useIsMobile();

  const load = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, chartRes, membRes] = await Promise.all([
        axios.get('/api/dashboard/stats').catch(() => ({ data: {} })),
        axios.get('/api/dashboard/today-bookings').catch(() => ({ data: {} })),
        axios.get('/api/dashboard/revenue-chart?days=7').catch(() => ({ data: [] })),
        axios.get('/api/memberships/subscriptions').catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data.stats || statsRes.data || {});
      setTodayBookings(bookingsRes.data.today || []);
      setUpcomingBookings(bookingsRes.data.upcoming || []);
      setRevenueChart(Array.isArray(chartRes.data) ? chartRes.data : chartRes.data.chart || []);
      const subs = membRes.data.subscriptions || [];
      setMemberships(subs.filter(s => {
        if (s.status !== 'active' || !s.next_billing) return false;
        const diff = (new Date(s.next_billing) - new Date()) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff <= 7;
      }));
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function completeBooking(booking) {
    try { await axios.put('/api/bookings/' + booking.id, { status: 'completed' }); load(); }
    catch(e) { alert('Error'); }
  }

  async function saveRebook() {
    if (!rebookDate) return alert('Select a date');
    setRebookSaving(true);
    try {
      await axios.post('/api/bookings', { contact_id: rebookTarget.contact_id, service_name: rebookTarget.service_name || rebookTarget.services?.name || 'Appointment', service_id: rebookTarget.service_id || null, start_time: rebookDate + 'T' + rebookTime + ':00', staff_id: rebookTarget.staff_id || null, notes: 'Rebooked', status: 'confirmed' });
      setRebookTarget(null); load();
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setRebookSaving(false);
  }

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', background: C.bg, color: C.muted }}>Loading...</div>;

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text, maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Top Header */}
      <div className='spa-header' style={{ background: C.panel, borderBottom: '1px solid ' + C.border, padding: isMobile ? '10px 14px' : '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>{bt === 'barbershop' || bt === 'barber' ? '💈' : '🌸'} {businessName || (bt === 'barbershop' || bt === 'barber' ? 'Barbershop' : 'Spa Dashboard')}</div>
        <div style={{ fontSize: 13, color: C.muted }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
      </div>

      {/* Tab Bar */}
      <div className='spa-tabs' style={{ background: C.panel, borderBottom: '1px solid ' + C.border, padding: isMobile ? '0 4px' : '0 20px', display: 'flex', gap: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setMainTab(t.key)} style={{ padding: isMobile ? '10px 8px' : '12px 18px', background: 'none', border: 'none', borderBottom: mainTab === t.key ? '2px solid ' + C.purple : '2px solid transparent', color: mainTab === t.key ? C.purple : C.muted, fontWeight: mainTab === t.key ? 800 : 600, cursor: 'pointer', fontSize: isMobile ? 11 : 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{t.icon}</span>{!isMobile && <span> {t.label}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className='spa-content' style={{ padding: isMobile ? '12px' : isTablet ? '16px' : '28px' }}>
        {mainTab === 'dashboard' && <DashboardTab stats={stats} todayBookings={todayBookings} upcomingBookings={upcomingBookings} revenueChart={revenueChart} memberships={memberships} onRebook={b => { setRebookTarget(b); setRebookDate(''); setRebookTime('10:00'); }} onComplete={completeBooking} setMainTab={setMainTab} reload={load} isMobile={isMobile} />}
        {mainTab === 'bookings' && <BookingsTab todayBookings={todayBookings} upcomingBookings={upcomingBookings} onRebook={b => { setRebookTarget(b); setRebookDate(''); setRebookTime('10:00'); }} onComplete={completeBooking} reload={load} isMobile={isMobile} />}
        {mainTab === 'clients' && <ClientsTab setMainTab={setMainTab} />}
        {mainTab === 'services' && <ServicesTab />}
        {mainTab === 'staff' && <StaffTab />}
        {mainTab === 'members' && <MembersTab />}
        {mainTab === 'reminders' && <RemindersTab />}
        {mainTab === 'notes' && <NotesTab />}
        {mainTab === 'finance' && <FinanceTab />}
      </div>

      {/* Rebook Modal */}
      {rebookTarget && (
        <Modal title="📅 Rebook Appointment" onClose={() => setRebookTarget(null)}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>{rebookTarget.contact_name || 'Client'} · {rebookTarget.service_name || 'Appointment'}</div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <Input label="Date" type="date" value={rebookDate} onChange={e => setRebookDate(e.target.value)} min={new Date().toISOString().split('T')[0]} style={{ flex: 1 }} />
            <Input label="Time" type="time" value={rebookTime} onChange={e => setRebookTime(e.target.value)} style={{ flex: 1 }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={() => setRebookTarget(null)} color={C.card} textColor={C.muted} style={{ border: '1px solid ' + C.border, flex: 1 }}>Cancel</Btn>
            <Btn onClick={saveRebook} disabled={rebookSaving || !rebookDate} style={{ flex: 2 }}>{rebookSaving ? 'Saving...' : '✅ Confirm Rebook'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
